<script setup lang="ts">
import type { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { EditorContent, useEditor } from '@tiptap/vue-3';
import { ref, watch } from 'vue';
import {
  decodeCrewBriefingRichTextValue,
  encodeCrewBriefingRichTextValue,
} from '@/charter/crew-briefing-rich-text.ts';

const props = withDefaults(
  defineProps<{
    modelValue: string;
    id: string;
    labelledBy: string;
    disabled?: boolean;
  }>(),
  {
    disabled: false,
  }
);

const emit = defineEmits<{
  'update:modelValue': [value: string];
}>();

const toolbarRevision = ref(0);

const editor = useEditor({
  content: decodeCrewBriefingRichTextValue(props.modelValue),
  editable: !props.disabled,
  extensions: [
    StarterKit.configure({
      blockquote: false,
      code: false,
      codeBlock: false,
      heading: false,
      horizontalRule: false,
      link: false,
      strike: false,
      trailingNode: false,
    }),
  ],
  editorProps: {
    attributes: {
      id: props.id,
      role: 'textbox',
      'aria-labelledby': props.labelledBy,
      'aria-multiline': 'true',
      class: 'crew-briefing-editor-content',
    },
  },
  onCreate: refreshToolbar,
  onSelectionUpdate: refreshToolbar,
  onTransaction: refreshToolbar,
  onUpdate: ({ editor: updatedEditor }) => {
    emit('update:modelValue', encodeCrewBriefingRichTextValue(updatedEditor.getJSON()));
  },
});

watch(
  () => props.modelValue,
  value => {
    if (!editor.value) {
      return;
    }

    const document = decodeCrewBriefingRichTextValue(value);
    if (JSON.stringify(editor.value.getJSON()) !== JSON.stringify(document)) {
      editor.value.commands.setContent(document, {
        emitUpdate: false,
        errorOnInvalidContent: true,
      });
    }
  }
);

watch(
  () => props.disabled,
  disabled => {
    editor.value?.setEditable(!disabled);
  }
);

function refreshToolbar(): void {
  toolbarRevision.value += 1;
}

function isActive(name: string): boolean {
  void toolbarRevision.value;
  return editor.value?.isActive(name) ?? false;
}

function run(command: (currentEditor: Editor) => void): void {
  if (!editor.value || props.disabled) {
    return;
  }

  command(editor.value);
}

function toggleBold(): void {
  run(currentEditor => currentEditor.chain().focus().toggleBold().run());
}

function toggleItalic(): void {
  run(currentEditor => currentEditor.chain().focus().toggleItalic().run());
}

function toggleUnderline(): void {
  run(currentEditor => currentEditor.chain().focus().toggleUnderline().run());
}

function toggleBulletList(): void {
  run(currentEditor => currentEditor.chain().focus().toggleBulletList().run());
}

function toggleOrderedList(): void {
  run(currentEditor => currentEditor.chain().focus().toggleOrderedList().run());
}

function clearFormatting(): void {
  run(currentEditor => currentEditor.chain().focus().unsetAllMarks().clearNodes().run());
}
</script>

<template>
  <div
    class="crew-briefing-editor p-component"
    :class="{ 'crew-briefing-editor-disabled': disabled, 'p-disabled': disabled }"
  >
    <div class="crew-briefing-editor-toolbar" role="toolbar" :aria-labelledby="labelledBy">
      <button
        type="button"
        title="Bold (Ctrl+B)"
        aria-label="Bold"
        :aria-controls="id"
        :aria-pressed="isActive('bold')"
        :class="{ active: isActive('bold') }"
        :disabled="disabled || !editor"
        @click="toggleBold"
      >
        <span class="font-bold" aria-hidden="true">B</span>
      </button>
      <button
        type="button"
        title="Italic (Ctrl+I)"
        aria-label="Italic"
        :aria-controls="id"
        :aria-pressed="isActive('italic')"
        :class="{ active: isActive('italic') }"
        :disabled="disabled || !editor"
        @click="toggleItalic"
      >
        <span class="italic" aria-hidden="true">I</span>
      </button>
      <button
        type="button"
        title="Underline (Ctrl+U)"
        aria-label="Underline"
        :aria-controls="id"
        :aria-pressed="isActive('underline')"
        :class="{ active: isActive('underline') }"
        :disabled="disabled || !editor"
        @click="toggleUnderline"
      >
        <span class="underline" aria-hidden="true">U</span>
      </button>
      <span class="crew-briefing-editor-separator" aria-hidden="true"></span>
      <button
        type="button"
        title="Bulleted list"
        aria-label="Bulleted list"
        :aria-controls="id"
        :aria-pressed="isActive('bulletList')"
        :class="{ active: isActive('bulletList') }"
        :disabled="disabled || !editor"
        @click="toggleBulletList"
      >
        <i class="pi pi-list" aria-hidden="true"></i>
      </button>
      <button
        type="button"
        title="Numbered list"
        aria-label="Numbered list"
        :aria-controls="id"
        :aria-pressed="isActive('orderedList')"
        :class="{ active: isActive('orderedList') }"
        :disabled="disabled || !editor"
        @click="toggleOrderedList"
      >
        <span class="text-xs font-semibold" aria-hidden="true">1.</span>
      </button>
      <span class="crew-briefing-editor-separator" aria-hidden="true"></span>
      <button
        type="button"
        title="Clear formatting"
        aria-label="Clear formatting"
        :aria-controls="id"
        :disabled="disabled || !editor"
        @click="clearFormatting"
      >
        <i class="pi pi-eraser" aria-hidden="true"></i>
      </button>
    </div>
    <EditorContent :editor="editor" />
  </div>
</template>

<style scoped>
.crew-briefing-editor {
  overflow: hidden;
  border: 1px solid #ced4da;
  border-radius: var(--radius, 0);
  background: #fff;
  color: var(--c_primary);
  transition:
    background-color 0.2s,
    color 0.2s,
    border-color 0.2s,
    box-shadow 0.2s;
}

.crew-briefing-editor:not(.crew-briefing-editor-disabled):hover {
  border-color: #000;
}

.crew-briefing-editor:focus-within {
  border-color: #000;
  box-shadow: 0 0 0 0.125rem #bfd1f6;
}

.crew-briefing-editor-disabled {
  cursor: not-allowed;
}

.crew-briefing-editor-toolbar {
  display: flex;
  min-height: 2.75rem;
  align-items: center;
  gap: 0.125rem;
  border-bottom: 1px solid #dee2e6;
  padding: 0.5rem 0.625rem;
  background: #f8f9fa;
}

.crew-briefing-editor-toolbar button {
  display: inline-flex;
  width: 1.75rem;
  height: 1.75rem;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: var(--radius, 0);
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  transition:
    color 0.2s,
    box-shadow 0.2s;
}

.crew-briefing-editor-toolbar button:hover:not(:disabled) {
  color: #495057;
}

.crew-briefing-editor-toolbar button:focus-visible {
  outline: 0;
  box-shadow: 0 0 0 0.125rem #bfd1f6;
}

.crew-briefing-editor-toolbar button.active {
  color: #000;
}

.crew-briefing-editor-toolbar button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.crew-briefing-editor-separator {
  width: 1px;
  height: 1.25rem;
  margin: 0 0.25rem;
  background: #dee2e6;
}

:deep(.crew-briefing-editor-content) {
  min-height: 8.75rem;
  padding: 0.75rem 0.625rem;
  outline: none;
  white-space: pre-wrap;
}

:deep(.crew-briefing-editor-content p) {
  margin: 0 0 0.3125rem;
}

:deep(.crew-briefing-editor-content p:last-child) {
  margin-bottom: 0;
}

:deep(.crew-briefing-editor-content ul) {
  margin: 0.15625rem 0;
  padding-left: 0.9375rem;
  list-style: disc;
}

:deep(.crew-briefing-editor-content ol) {
  margin: 0.15625rem 0;
  padding-left: 0.9375rem;
  list-style: decimal;
}
</style>
